<template>
  <Sheet
    v-model="isOpen"
    :title="sheetTitle"
    :close-icon="['fas', 'check']"
    size="lg">
    <div class="px-0 pb-4 space-y-5">
      <!-- Current status info banner -->
      <div
        v-if="currentLog && statusInfo"
        class="flex items-center gap-3 px-4 py-3 rounded-lg"
        :class="statusBannerClass">
        <fa-icon :icon="statusInfo.icon" class="text-lg" />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium">{{ statusInfo.label }}</p>
          <p class="text-xs opacity-70">{{ statusInfo.date }}</p>
        </div>
      </div>

      <!-- Date watched (hidden for watchlist) -->
      <div v-if="showDateField" class="space-y-2">
        <label class="text-sm font-medium text-white/70"
          >When did you watch?</label
        >
        <DateQuickPicker v-model="watchedDate" />
      </div>

      <!-- Streaming service -->
      <div class="space-y-2">
        <label class="text-sm font-medium text-white/70">{{
          status === 'watchlist'
            ? 'Where will you watch?'
            : 'Where did you watch?'
        }}</label>
        <input
          v-model="serviceSearch"
          type="text"
          placeholder="Search services..."
          class="w-full px-3 py-2 text-sm text-white rounded-lg bg-white/5 focus:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 placeholder:text-white/40" />
        <div class="flex flex-wrap gap-2">
          <button
            v-for="service in filteredServices"
            :key="service.id"
            type="button"
            class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors rounded-lg cursor-pointer"
            :class="[
              selectedService === service.id
                ? 'bg-white/20 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
            ]"
            @click="selectService(service)">
            <img
              v-if="service.logo"
              :src="service.logo"
              :alt="service.name"
              class="w-4 h-4 rounded-sm object-contain"
              loading="lazy" />
            {{ service.name }}
          </button>
          <button
            type="button"
            class="px-3 py-2 text-sm font-medium transition-colors rounded-lg cursor-pointer"
            :class="[
              showCustomService
                ? 'bg-white/20 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
            ]"
            @click="toggleCustomService">
            Other...
          </button>
        </div>
        <input
          v-if="showCustomService"
          v-model="customService"
          type="text"
          placeholder="Enter service name"
          class="w-full px-4 py-3 text-sm text-white rounded-lg bg-white/5 focus:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 placeholder:text-white/40" />
      </div>

      <!-- TV Episode selection (only for TV shows) -->
      <div v-if="isTVShow" class="space-y-2">
        <label class="text-sm font-medium text-white/70">Which episode?</label>
        <div class="flex gap-3">
          <div class="flex-1 space-y-1">
            <span class="text-xs text-white/50">Season</span>
            <input
              v-model.number="tvSeason"
              type="number"
              min="1"
              placeholder="1"
              class="w-full px-4 py-3 text-sm text-white rounded-lg bg-white/5 focus:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 placeholder:text-white/40" />
          </div>
          <div class="flex-1 space-y-1">
            <span class="text-xs text-white/50">Episode</span>
            <input
              v-model.number="tvEpisode"
              type="number"
              min="1"
              placeholder="1"
              class="w-full px-4 py-3 text-sm text-white rounded-lg bg-white/5 focus:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 placeholder:text-white/40" />
          </div>
        </div>
      </div>

      <!-- Notes (optional) -->
      <div class="space-y-2">
        <label class="text-sm font-medium text-white/70"
          >Notes (optional)</label
        >
        <textarea
          v-model="notes"
          rows="2"
          placeholder="Any thoughts or notes..."
          class="w-full px-4 py-3 text-sm text-white rounded-lg resize-none bg-white/5 focus:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 placeholder:text-white/40" />
      </div>

      <!-- Action buttons -->
      <div class="flex gap-3 pt-2">
        <button
          type="button"
          class="flex-1 px-4 py-3 text-xs font-medium transition-colors rounded-lg cursor-pointer text-white/70 bg-white/5 hover:bg-white/10 hover:text-white"
          @click="handleSkip">
          Skip
        </button>
        <button
          type="button"
          class="flex-1 px-4 py-3 text-xs font-medium text-white transition-colors rounded-lg cursor-pointer"
          :class="saveButtonClass"
          @click="handleSave">
          <fa-icon :icon="saveButtonIcon" class="mr-2" />
          {{ saveButtonLabel }}
        </button>
      </div>
    </div>
  </Sheet>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { formatTimeAgo } from '@/use/useFormatDate'
import { Sheet } from '@/components/Options'
import DateQuickPicker from '@/components/Pure/DateQuickPicker.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  status: {
    type: String,
    required: true,
    validator: (v) => ['seen', 'in_progress', 'watchlist'].includes(v)
  },
  showDateField: {
    type: Boolean,
    default: true
  },
  isRewatch: {
    type: Boolean,
    default: false
  },
  currentLog: {
    type: Object,
    default: null
  },
  titleType: {
    type: String,
    default: 'movie'
  },
  titleName: {
    type: String,
    default: ''
  },
  title: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'save', 'skip'])

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Current status info for banner
const statusInfo = computed(() => {
  if (!props.currentLog) return null

  const state = props.currentLog.state
  const stateChangedAt = props.currentLog.state_changed_at
  const createdAt = props.currentLog.created_at

  switch (state) {
    case 'watchlist':
      return {
        label: 'On your watchlist',
        date: `Added ${formatTimeAgo(createdAt)}`,
        icon: ['fas', 'bookmark']
      }
    case 'in_progress':
      return {
        label: 'Currently watching',
        date: `Started ${formatTimeAgo(stateChangedAt)}`,
        icon: ['fas', 'play-circle']
      }
    case 'seen':
      return {
        label: 'Already watched',
        date: `Marked seen ${formatTimeAgo(stateChangedAt)}`,
        icon: ['fas', 'hexagon-check']
      }
    default:
      return null
  }
})

const statusBannerClass = computed(() => {
  if (!props.currentLog) return ''

  switch (props.currentLog.state) {
    case 'watchlist':
      return 'bg-amber-500/20 text-amber-400'
    case 'in_progress':
      return 'bg-cyan-500/20 text-cyan-400'
    case 'seen':
      return 'bg-green-500/20 text-green-400'
    default:
      return 'bg-white/10 text-white/70'
  }
})

// Check if this is a rewatch (explicit prop or marking as seen when already seen)
const isRewatchMode = computed(() => {
  return (
    props.isRewatch ||
    (props.status === 'seen' && props.currentLog?.state === 'seen')
  )
})

// Local date string helper (avoids UTC timezone shift from toISOString)
function toLocalDateString(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// Date selection
const watchedDate = ref(toLocalDateString(new Date()))

// Service search and selection
const serviceSearch = ref('')
const selectedService = ref(null)
const showCustomService = ref(false)
const customService = ref('')

// Extract TMDB watch providers
const tmdbProviders = computed(() => {
  if (!props.title?.['watch/providers']?.results) return []
  const services = new Map()
  Object.values(props.title['watch/providers'].results).forEach((region) => {
    ;['flatrate', 'rent', 'buy'].forEach((type) => {
      if (region[type]) {
        region[type].forEach((provider) => {
          if (!services.has(provider.provider_id)) {
            services.set(provider.provider_id, {
              id: String(provider.provider_id),
              name: provider.provider_name,
              logo: provider.logo_path
                ? `https://image.tmdb.org/t/p/w92${provider.logo_path}`
                : null
            })
          }
        })
      }
    })
  })
  return Array.from(services.values())
})

const fallbackServices = [
  { id: 'netflix', name: 'Netflix', logo: 'https://image.tmdb.org/t/p/w92/t2yyOv40HZeVlLjYsCsPHnWLk4W.jpg' },
  { id: 'prime', name: 'Prime Video', logo: 'https://image.tmdb.org/t/p/w92/68MNrwlkpF7WnmNPXLah69CR5cb.jpg' },
  { id: 'disney', name: 'Disney+', logo: 'https://image.tmdb.org/t/p/w92/7rwgEs15tFwyR9NPQ5vpzxTj19Q.jpg' },
  { id: 'apple', name: 'Apple TV+', logo: 'https://image.tmdb.org/t/p/w92/6uhKBfmtzFqOcLousHwZuzcrScK.jpg' },
  { id: 'hbo', name: 'Max', logo: 'https://image.tmdb.org/t/p/w92/aS2zvJWn9mwiCOeaaCkIh4wleZS.jpg' },
  { id: 'hulu', name: 'Hulu', logo: 'https://image.tmdb.org/t/p/w92/giwM8XX4V2AQb9vsoN7yti82tKK.jpg' },
  { id: 'paramount', name: 'Paramount+', logo: 'https://image.tmdb.org/t/p/w92/xbhHHa1YgtpwhC8lb1NQ3ACVcLd.jpg' },
  { id: 'peacock', name: 'Peacock', logo: 'https://image.tmdb.org/t/p/w92/8VCV78prwd9QzZnEm0ReO6bERDa.jpg' },
  { id: 'mubi', name: 'MUBI', logo: null },
  { id: 'cinema', name: 'Cinema', logo: null }
]

const allServices = computed(() => {
  return tmdbProviders.value.length > 0 ? tmdbProviders.value : fallbackServices
})

const filteredServices = computed(() => {
  if (!serviceSearch.value) return allServices.value
  const q = serviceSearch.value.toLowerCase()
  return allServices.value.filter((s) => s.name.toLowerCase().includes(q))
})

const selectService = (service) => {
  selectedService.value = service.id
  showCustomService.value = false
  serviceSearch.value = ''
}

const toggleCustomService = () => {
  showCustomService.value = !showCustomService.value
  if (showCustomService.value) {
    selectedService.value = null
    serviceSearch.value = ''
  }
}

// TV episode info
const tvSeason = ref(null)
const tvEpisode = ref(null)

const isTVShow = computed(() => props.titleType === 'tv')

// Notes
const notes = ref('')

// Computed title for sheet
const sheetTitle = computed(() => {
  if (props.status === 'seen') {
    return isRewatchMode.value ? 'Log Rewatch' : 'Mark as Seen'
  } else if (props.status === 'in_progress') {
    return 'Start Watching'
  } else if (props.status === 'watchlist') {
    return 'Add to Watchlist'
  }
  return 'Watch Status'
})

// Save button styling based on status
const saveButtonClass = computed(() => {
  if (props.status === 'seen') {
    return 'bg-green-500 hover:bg-green-400'
  } else if (props.status === 'in_progress') {
    return 'bg-cyan-500 hover:bg-cyan-400'
  } else if (props.status === 'watchlist') {
    return 'bg-amber-500 hover:bg-amber-400'
  }
  return 'bg-amber-500 hover:bg-amber-400'
})

const saveButtonIcon = computed(() => {
  if (props.status === 'seen') {
    return isRewatchMode.value
      ? ['fas', 'rotate-right']
      : ['fas', 'hexagon-check']
  } else if (props.status === 'in_progress') {
    return ['fas', 'play-circle']
  } else if (props.status === 'watchlist') {
    return ['fas', 'bookmark']
  }
  return ['fas', 'bookmark']
})

const saveButtonLabel = computed(() => {
  if (props.status === 'seen') {
    return isRewatchMode.value ? 'Log Rewatch' : 'Mark Seen'
  } else if (props.status === 'in_progress') {
    return 'Start Watching'
  } else if (props.status === 'watchlist') {
    return 'Add to Watchlist'
  }
  return 'Save'
})

// Get the final date value
const getWatchedDate = () => {
  return watchedDate.value || toLocalDateString(new Date())
}

// Get the final service value
const getService = () => {
  if (showCustomService.value && customService.value) {
    return customService.value
  }
  if (selectedService.value) {
    const service = allServices.value.find((s) => s.id === selectedService.value)
    return service?.name || selectedService.value
  }
  return null
}

// Handle save
const handleSave = () => {
  const metadata = {
    watchedOn: getWatchedDate(),
    watchedService: getService(),
    notes: notes.value || null,
    tvSeason: isTVShow.value ? tvSeason.value : null,
    tvEpisode: isTVShow.value ? tvEpisode.value : null
  }
  emit('save', metadata)
  isOpen.value = false
}

// Handle skip (just set status without metadata)
const handleSkip = () => {
  emit('skip')
  isOpen.value = false
}

// Helper to match service name to known service ID
const matchServiceToId = (serviceName) => {
  if (!serviceName) return null
  const lowerName = serviceName.toLowerCase()
  const service = allServices.value.find(
    (s) => s.name.toLowerCase() === lowerName || s.id === lowerName
  )
  return service?.id || null
}

// Pre-populate form from currentLog when sheet opens
watch(isOpen, (newValue) => {
  if (newValue) {
    const log = props.currentLog

    // Pre-populate date from log
    if (log?.watched_on) {
      watchedDate.value = log.watched_on.split('T')[0]
    } else {
      watchedDate.value = toLocalDateString(new Date())
    }

    // Pre-populate service from log
    if (log?.watched_service) {
      const matchedId = matchServiceToId(log.watched_service)
      if (matchedId) {
        selectedService.value = matchedId
        showCustomService.value = false
        customService.value = ''
      } else {
        // Custom service - show input with value
        selectedService.value = null
        showCustomService.value = true
        customService.value = log.watched_service
      }
    } else {
      selectedService.value = null
      showCustomService.value = false
      customService.value = ''
    }
    serviceSearch.value = ''

    // Pre-populate notes from log
    notes.value = log?.notes || ''

    // Pre-populate TV episode info from log
    tvSeason.value = log?.tv_season || null
    tvEpisode.value = log?.tv_episode || null
  }
})
</script>
